import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { DATA_NEW_PROFIT_SUPPLIERS, UID_NAME_TUPLES } from 'constants/dataSets';
import {
  PROFIT_CENTER_LINKS,
} from 'constants/formFields';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import CostFields from './components/CostFields';
import DateSection from './components/DateSection';
import ProfitCenterSelector from './components/ProfitCenterSelector';

import type { TDate } from 'components/DatePicker/interfaces/TDate';
import type IProfitCenterLink from 'store/slices/dataSets/interfaces/IProfitCenterLink';
import type IProfitCenterLinksProps from './interfaces/IProfitCenterLinksProps';

import styles from './sass/ProfitCenterLinks.module.scss';

function ProfitCenterLinks({
  control,
  profitCenterIds,
  placeholder = '',
  watch,
  setValue,
}: Readonly<IProfitCenterLinksProps>) {
  const { t } = useTranslation();
  const { recordId = '' } = useParams();
  const profitCenterLinks = watch(PROFIT_CENTER_LINKS) || [];
  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;
  const profitCenterOptions = profitCenterIds?.[UID_NAME_TUPLES.profitCenter] || [];
  const maxProfitCenters = profitCenterOptions.length;

  const handleAddProfitCenter = () => {
    if (profitCenterLinks.length >= maxProfitCenters) {
      return;
    }
    const newProfitCenter: IProfitCenterLink = {
      ...DATA_NEW_PROFIT_SUPPLIERS,
    };

    setValue(PROFIT_CENTER_LINKS, [...profitCenterLinks, newProfitCenter]);
  };

  const handleRemoveProfitCenter = (index: number) => {
    const updatedLinks = [...profitCenterLinks];
    updatedLinks.splice(index, 1);
    setValue(PROFIT_CENTER_LINKS, updatedLinks);
  };

  const updateProfitCenterField = (index: number, field: keyof IProfitCenterLink, value: string | number | TDate) => {
    const updatedLinks = [...profitCenterLinks];
    updatedLinks[index] = {
      ...updatedLinks[index],
      [field]: value,
    };
    setValue(PROFIT_CENTER_LINKS, updatedLinks);
  };

  const handleProfitCenterChange = (index: number, field: keyof IProfitCenterLink, value: string) => {
    updateProfitCenterField(index, field, value);
  };

  if (profitCenterLinks.length === 0 && maxProfitCenters > 0) {
    handleAddProfitCenter();
  }

  return (

    <div className={styles.container}>
      {profitCenterLinks.map((profitCenter: IProfitCenterLink, index: number) => {
        const profitCenterId = `profit-center-${profitCenter.profitCenterId || index}`;
        return (
          <div key={profitCenterId} className={styles.profitCenterItem}>
            <h3>
              {t('common.profitCentre', { number: index + 1 })}
            </h3>

            {profitCenterLinks.length > 1 && index !== 0 && (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => handleRemoveProfitCenter(index)}
                aria-label={t('common.delete')}
              >
                <TrashIcon />
              </button>
            )}

            <ProfitCenterSelector
              control={control}
              index={index}
              placeholder={placeholder}
              profitCenterOptions={profitCenterIds?.[UID_NAME_TUPLES.profitCenter]}
              recordId={recordId}
              onProfitCenterChange={handleProfitCenterChange}
            />
            <DateSection
              control={control}
              index={index}
              startDate={profitCenter.startDate}
              onStartDateChange={(date) => updateProfitCenterField(index, 'startDate', date)}
              onEndDateChange={(date) => updateProfitCenterField(index, 'endDate', date)}
            />
            <CostFields
              control={control}
              index={index}
              placeholder={placeholder}
              companyCurrency={companyCurrency}
              onTotalCostChange={(value) => updateProfitCenterField(index, 'totalCost', value)}
              onPercentChange={(value) => updateProfitCenterField(index, 'percentOfTotalCost', value)}
            />
          </div>
        );
      })}

      {profitCenterLinks.length < maxProfitCenters && (
        <button
          type="button"
          className={styles.addButton}
          onClick={handleAddProfitCenter}
          aria-label={t('common.add')}
        >
          <PlusIcon />
          {t('common.addProfitCentre')}
        </button>
      )}
    </div>

  );
}

export default ProfitCenterLinks;
